// =============================================================================
// Origin UI — src/services/complianceApi.ts
// API service for the Compliance module endpoints (CM-2 / CM-3 / CM-4 / CM-8).
// All calls go through the core `api` wrapper (apiService.ts) so the bearer
// token and VITE_API_BASE_URL are injected automatically.
// =============================================================================

import { api } from './apiService';

// ─── Shared enums ───

export type ComplianceItemType = 'Attestation' | 'Declaration' | 'Policy';

// ─── Form field schema ───
// Field types locked in CM-3 (the server-side validator is the contract).
// The schema is a BARE ARRAY of these field objects (confirmed against the
// seeded COC schema). Parsed by the FormRenderer (CM-7).
export type ComplianceFieldType =
  | 'ShortText'
  | 'LongText'
  | 'Number'
  | 'Currency'
  | 'Date'
  | 'SingleSelect'
  | 'MultiSelect'
  | 'Confirmation'
  | 'StaticText';

export interface ComplianceFormOption {
  value: string;
  label: string;
}

export interface ComplianceFormField {
  key: string;
  label: string;
  helpText?: string;
  type: ComplianceFieldType;
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  minDate?: string; // ISO date OR the literal "today"
  maxDate?: string; // ISO date OR the literal "today"
  options?: ComplianceFormOption[];
}

// ─── Gating steps ───
// Shape confirmed against GatingStepConfig: { step, enabled, order }.
export interface ComplianceGatingStep {
  step: string; // "Video" | "Document" | "Form"
  enabled: boolean;
  order: number;
}

// ─── Employee / shared read types ───

export interface ComplianceItemListItem {
  complianceItemId: string;
  code: string;
  title: string;
  itemType: ComplianceItemType;
  isActive: boolean;
  hasCurrentVersion: boolean;
  synthesiaVideoId: string | null;
}

export interface ComplianceItemDetail {
  complianceItemId: string;
  code: string;
  title: string;
  itemType: ComplianceItemType;
  isActive: boolean;
  synthesiaVideoId: string | null;
  gatingSteps: ComplianceGatingStep[];
  currentVersionId: string | null;
  currentVersionNumber: number | null;
  formSchema: string | null; // raw JSON string — parsed by FormRenderer
  documentBlobPath: string | null;
}

export interface ComplianceOutstandingItem {
  complianceAssignmentId: string;
  complianceItemId: string;
  complianceItemVersionId: string;
  code: string;
  title: string;
  itemType: ComplianceItemType;
  assignedDate: string;
  synthesiaVideoId: string | null;
}

export interface ComplianceSubmissionListItem {
  complianceSubmissionId: string;
  complianceItemId: string;
  complianceItemVersionId: string;
  code: string;
  title: string;
  submissionType: ComplianceItemType;
  versionNumber: number;
  submittedDate: string;
}

export interface ComplianceSubmitRequest {
  complianceItemId: string;
  answers: Record<string, unknown>;
}

export interface ComplianceSubmitResult {
  submissionId: string;
  message: string;
}

// ─── Admin types ───

export interface ComplianceItemAdminListItem {
  complianceItemId: string;
  code: string;
  title: string;
  itemType: ComplianceItemType;
  isActive: boolean;
  currentVersionId: string | null;
  currentVersionNumber: number | null;
  versionCount: number;
  synthesiaVideoId: string | null;
  reviewIntervalMonths: number | null;
  // CR-7: Automated reminder config
  reminderEnabled: boolean;
  firstReminderDays: number;
  secondReminderDays: number;
}

export interface CreateComplianceItemDto {
  code: string;
  title: string;
  itemType: ComplianceItemType;
  synthesiaVideoId?: string | null;
  gatingSteps?: ComplianceGatingStep[];
}

export interface UpdateComplianceItemDto {
  title: string;
  isActive: boolean;
  synthesiaVideoId?: string | null;
  gatingSteps?: ComplianceGatingStep[];
  reviewIntervalMonths?: number | null;
  // CR-7: Automated reminder config
  reminderEnabled?: boolean;
  firstReminderDays?: number;
  secondReminderDays?: number;
}

export interface CreateComplianceItemResult {
  complianceItemId: string;
  message: string;
}

export interface ComplianceItemVersionDto {
  complianceItemVersionId: string;
  complianceItemId: string;
  versionNumber: number;
  formSchema: string | null;
  documentBlobPath: string | null;
  publishedAt: string;
  publishedBy: string | null;
}

export interface AssignNowResult {
  assignedCount: number;
  supersededCount: number;
}

export interface ComplianceArticle {
  complianceArticleId: string;
  title: string;
  body: string;
  publishedAt: string;
  publishedBy: string | null;
  isActive: boolean;
}

export interface CreateComplianceArticleDto {
  title: string;
  body: string;
}

export interface UpdateComplianceArticleDto {
  title: string;
  body: string;
  isActive: boolean;
}

// ─── Dashboard types (CM-11) ───

export type ComplianceAssignmentStatus = 'Outstanding' | 'Completed';

export interface ComplianceCompletionRow {
  complianceSubmissionId: string | null;
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  department: string;
  office: string;
  status: ComplianceAssignmentStatus;
  completedDate: string | null;
  reminderStatus: string;
  lastReminderDate: string | null;
}

export interface ComplianceCompletion {
  complianceItemId: string;
  code: string;
  title: string;
  currentVersionNumber: number | null;
  totalAssigned: number;
  completedCount: number;
  rows: ComplianceCompletionRow[];
}

export interface ComplianceDeclarationLogItem {
  complianceSubmissionId: string;
  complianceItemId: string;
  code: string;
  title: string;
  versionNumber: number;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  department: string;
  office: string;
  submittedDate: string;
}

export interface ComplianceSubmissionDetail {
  complianceSubmissionId: string;
  complianceItemId: string;
  code: string;
  title: string;
  submissionType: ComplianceItemType;
  versionNumber: number;
  submittedDate: string;
  employeeId: string;
  employeeName: string;
  formSchema: string | null;
  answers: string | null;
}

export interface CreateComplianceArticleResult {
  complianceArticleId: string;
  message: string;
}

// ─── Review reminders (CV-4) ───

export interface ComplianceReviewDueItem {
  complianceItemId: string;
  code: string;
  title: string;
  lastSubmissionDate: string;
  reviewDueDate: string;
}

export interface ConfirmReviewRequest {
  complianceItemId: string;
}

export interface ConfirmReviewResult {
  submissionId: string;
  message: string;
}

// ─── Team (Meet the team panel) ───

export interface ComplianceTeamMember {
  employeeId: string;
  name: string;
  jobTitle: string;
  office: string;
  phone: string | null;
}

export interface ComplianceDocument {
  complianceItemId: string;
  code: string;
  title: string;
  itemType: ComplianceItemType;
  fileName: string;
}

// ─── API ───

export const complianceApi = {
  // ── Employee / shared ──
  getItems: () => api.get<ComplianceItemListItem[]>('/api/compliance'),

  getItem: (itemId: string) =>
    api.get<ComplianceItemDetail>(`/api/compliance/${itemId}`),

  getMyOutstanding: () =>
    api.get<ComplianceOutstandingItem[]>('/api/compliance/outstanding/mine'),

  getMySubmissions: () =>
    api.get<ComplianceSubmissionListItem[]>('/api/compliance/submissions/mine'),

  // Review reminders — declarations due for periodic review (CV-4).
  getMyReviewsDue: () =>
    api.get<ComplianceReviewDueItem[]>('/api/compliance/reviews/mine'),

  // Confirm no changes on a review-due declaration (resets the review clock).
  confirmReview: (complianceItemId: string) =>
    api.post<ConfirmReviewResult>('/api/compliance/reviews/confirm', { complianceItemId }),

  // Published articles for the all-staff Compliance landing page.
  getArticles: () =>
    api.get<ComplianceArticle[]>('/api/compliance/articles'),

  // Compliance-role members for the landing "Meet the team" panel.
  getTeam: () =>
    api.get<ComplianceTeamMember[]>('/api/compliance/team'),

  // Compliance documents available to read (always-visible repository).
  getDocuments: () =>
    api.get<ComplianceDocument[]>('/api/compliance/documents'),

  // Full content of one submission (self, or any submission for Compliance).
  getSubmissionDetail: (submissionId: string) =>
    api.get<ComplianceSubmissionDetail>(`/api/compliance/submissions/${submissionId}`),

  submit: (request: ComplianceSubmitRequest) =>
    api.post<ComplianceSubmitResult>('/api/compliance/submit', request),

  // Fetch the current version's document (e.g. the Code of Conduct PDF) as a
  // Blob for in-app viewing. Mirrors the authenticated-fetch pattern in letterApi.
  getDocumentBlob: async (itemId: string): Promise<Blob> => {
    const token = await api.getToken();
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || ''}/api/compliance/${itemId}/document`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!response.ok) {
      throw new Error(`Document download failed (${response.status})`);
    }
    return await response.blob();
  },

  // Open a compliance item's current-version document full-page in a new tab.
  // The download endpoint needs a bearer token, so a plain target="_blank" link
  // won't work — we fetch the PDF with auth, then hand the blob to a new tab via
  // an object URL. The tab is opened synchronously (before the await) so popup
  // blockers don't catch it.
  openDocument: async (itemId: string): Promise<void> => {
    const win = window.open('', '_blank');
    try {
      const blob = await complianceApi.getDocumentBlob(itemId);
      const url = URL.createObjectURL(blob);
      // #toolbar=0 hides the built-in viewer's download/print toolbar in
      // Chromium browsers. Not a hard guarantee (the browser must load the
      // bytes to render), but it removes the obvious save path.
      const viewUrl = `${url}#toolbar=0&navpanes=0`;
      if (win) win.location.href = viewUrl;
      else window.open(viewUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      throw err;
    }
  },

  // ── Admin (Compliance role only) ──
  adminGetItems: () =>
    api.get<ComplianceItemAdminListItem[]>('/api/compliance/admin/items'),

  adminCreateItem: (data: CreateComplianceItemDto) =>
    api.post<CreateComplianceItemResult>('/api/compliance/admin/items', data),

  adminUpdateItem: (id: string, data: UpdateComplianceItemDto) =>
    api.put<void>(`/api/compliance/admin/items/${id}`, data),

  adminDeleteItem: (id: string) =>
    api.delete<void>(`/api/compliance/admin/items/${id}`),

  // Publish a new version — multipart: formSchema (JSON string) + optional PDF.
  adminPublishVersion: (itemId: string, formSchema: string, document?: File) => {
    const fd = new FormData();
    fd.append('formSchema', formSchema);
    if (document) fd.append('document', document);
    return api.upload<ComplianceItemVersionDto>(
      `/api/compliance/admin/items/${itemId}/versions`,
      fd,
    );
  },

  adminAssignNow: (itemId: string) =>
    api.post<AssignNowResult>(`/api/compliance/admin/items/${itemId}/assign`),

  // ── Dashboard (Compliance) ──
  adminGetCompletion: (itemId: string) =>
    api.get<ComplianceCompletion>(`/api/compliance/admin/items/${itemId}/completion`),

  adminGetDeclarationsLog: () =>
    api.get<ComplianceDeclarationLogItem[]>('/api/compliance/admin/declarations'),

  // ── Admin — articles ──
  adminGetArticles: () =>
    api.get<ComplianceArticle[]>('/api/compliance/admin/articles'),

  adminCreateArticle: (data: CreateComplianceArticleDto) =>
    api.post<CreateComplianceArticleResult>('/api/compliance/admin/articles', data),

  adminUpdateArticle: (id: string, data: UpdateComplianceArticleDto) =>
    api.put<{ message: string }>(`/api/compliance/admin/articles/${id}`, data),

  adminDeleteArticle: (id: string) =>
    api.delete<void>(`/api/compliance/admin/articles/${id}`),

  // ── Reminder history (CR-9) ──
  getReminderHistory: (params?: { itemId?: string; employeeId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.itemId) qs.set('itemId', params.itemId);
    if (params?.employeeId) qs.set('employeeId', params.employeeId);
    const query = qs.toString();
    return api.get<ComplianceReminderHistoryItem[]>(
      `/api/compliance-reminders/history${query ? `?${query}` : ''}`
    );
  },
};

// ─── Reminder history types (CR-9) ───

export interface ComplianceReminderHistoryItem {
  complianceReminderId: string;
  complianceAssignmentId: string;
  employeeId: string;
  employeeName: string;
  reminderType: string;
  sentDate: string;
  recipientEmail: string | null;
  escalationDate: string | null;
  managerName: string | null;
}
